import { LuInfinity, LuStar, LuHeart, LuCoins, LuSwords } from "react-icons/lu";
import { MAP_DEFS, MAP_IDS, COLS, ROWS, TUNE, WAVES, getMap, type MapId } from "../../shared/games/taarn";

/** A little top-down drawing of a map's route (used on the map cards and nowhere else needs WebGL). */
export function MapPreview({ id }: { id: MapId }) {
  const d = MAP_DEFS[id];
  const m = getMap(id);
  const p = d.palette;
  const pts = d.waypoints.map(([x, y]) => `${x + 0.5},${y + 0.5}`).join(" ");
  const [sx, sy] = d.waypoints[0]!;
  const [gx, gy] = d.waypoints[d.waypoints.length - 1]!;
  const specks: [number, number][] = [];
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) if (!m.isPath(x, y) && (x * 7 + y * 13 + x * y) % 5 === 0) specks.push([x + 0.5 + (((x * 31 + y * 17) % 7) - 3) / 14, y + 0.5 + (((x * 11 + y * 29) % 7) - 3) / 14]);
  return (
    <svg className="mapprev" viewBox={`-0.15 -0.15 ${COLS + 0.3} ${ROWS + 0.3}`} role="img" aria-label={`Kort over ${d.name}`}>
      <defs>
        <linearGradient id={`mp-g-${id}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={p.ground} /><stop offset="1" stopColor={p.ground2} /></linearGradient>
      </defs>
      <rect x="0" y="0" width={COLS} height={ROWS} rx="0.7" fill={`url(#mp-g-${id})`} />
      {specks.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={id === "slik" ? 0.12 : 0.09} fill={i % 3 === 0 ? p.accent : p.ink} opacity={id === "slik" || id === "vinter" ? 0.7 : 0.28} />)}
      <polyline points={pts} fill="none" stroke={p.road2} strokeWidth="1.18" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={pts} fill="none" stroke={p.road} strokeWidth="0.92" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={pts} fill="none" stroke={p.accent} strokeWidth="0.1" strokeDasharray="0.35 0.35" strokeLinejoin="round" opacity="0.9" />
      <circle cx={sx + 0.55} cy={sy + 0.5} r="0.62" fill="#2a1f3d" stroke="#fff" strokeWidth="0.12" />
      <path d={`M${gx + 0.1} ${gy + 1.05} v-1.1 l0.35 0.3 l0.35 -0.3 l0.35 0.3 l0.35 -0.3 v1.1 z`} fill="#fff" stroke="#2a1f3d" strokeWidth="0.12" strokeLinejoin="round" />
      <circle cx={gx + 0.62} cy={gy + 0.3} r="0.2" fill={p.accent} stroke="#2a1f3d" strokeWidth="0.08" />
    </svg>
  );
}

const Stars = ({ n }: { n: number }) => (
  <span className="stars" aria-label={`${n} af 3 stjerner`}>{[1, 2, 3].map((i) => <LuStar key={i} className={i <= n ? "on" : ""} aria-hidden="true" />)}</span>
);

export function MapPicker({ value, onChange }: { value: MapId; onChange: (m: MapId) => void }) {
  return (
    <div>
      <span className="section-label">Vælg bane</span>
      <div className="mapgrid" role="radiogroup" aria-label="Bane">
        {MAP_IDS.map((id) => {
          const d = MAP_DEFS[id];
          const on = value === id;
          return (
            <button type="button" key={id} role="radio" aria-checked={on} className="mapcard" data-on={on} data-map={id} onClick={() => onChange(id)}>
              <MapPreview id={id} />
              <span className="mapname"><b>{d.name}</b><Stars n={d.stars} /></span>
              <span className="maptag">{d.tagline}</span>
              <span className="mapfromto"><span className="mapdot m-skov" style={{ background: d.palette.accent }} /> {d.start} <i aria-hidden="true">→</i> {d.goal}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DIFFS = [
  { d: 1 as const, color: "#2ee6a6", q: "Lette spørgsmål", extra: "Et roligt spil med tid til at tænke." },
  { d: 2 as const, color: "#ffb347", q: "Mellemsvære spørgsmål", extra: "En fair kamp." },
  { d: 3 as const, color: "#ff5f6d", q: "Svære spørgsmål – mest guld", extra: "Hurtigere og sejere balloner." },
];
const LABEL = { 1: "Let", 2: "Mellem", 3: "Svær" } as const;

export function DifficultyPicker({ value, onChange }: { value: 1 | 2 | 3; onChange: (d: 1 | 2 | 3) => void }) {
  return (
    <div>
      <span className="section-label">Vælg sværhedsgrad</span>
      <p className="muted" style={{ margin: "0 0 8px" }}>Sværhed ændrer både <b>spørgsmål</b> og <b>bane</b>.</p>
      <div className="diffgrid" role="radiogroup" aria-label="Sværhedsgrad">
        {DIFFS.map(({ d, color, q, extra }) => {
          const t = TUNE[d];
          return (
            <button type="button" key={d} role="radio" aria-checked={value === d} className="diffcard" data-on={value === d} style={{ ["--dc" as any]: color }} onClick={() => onChange(d)}>
              <b className="dname">{LABEL[d]}</b>
              <span className="dq">{q}</span>
              <ul className="dstats">
                <li><LuHeart aria-hidden="true" /> {t.lives} liv</li>
                <li><LuCoins aria-hidden="true" /> {t.cash} guld at starte med</li>
                <li><LuSwords aria-hidden="true" /> {Math.round(t.count * 100)} % balloner · {Math.round(t.speed * 100)} % fart</li>
                <li><LuInfinity aria-hidden="true" /> Efter runde {WAVES}: {d === 1 ? "blid" : d === 2 ? "stadig sværere" : "hurtigt sværere"}</li>
              </ul>
              <span className="dextra">{extra}</span>
            </button>
          );
        })}
      </div>
      <p className="muted" style={{ margin: "8px 0 0" }}>Efter runde {WAVES} kan du <b>spille videre</b> – runderne bliver sværere.</p>
    </div>
  );
}
